import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Reveal, MaskReveal } from "@/components/Reveal";
import { IMAGES } from "@/data/site";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSES = [
  {
    id: "manikure",
    nameKey: "schulungen.c1_name", tagKey: "schulungen.c1_tag", priceKey: "schulungen.c1_price", descKey: "schulungen.c1_desc",
    bulletKeys: ["schulungen.c1_b1", "schulungen.c1_b2", "schulungen.c1_b3", "schulungen.c1_b4", "schulungen.c_hygiene"],
    forKey: "schulungen.c1_for", ctaKey: "schulungen.c1_cta",
  },
  {
    id: "pedikure",
    nameKey: "schulungen.c2_name", tagKey: "schulungen.c2_tag", priceKey: "schulungen.c2_price", descKey: "schulungen.c2_desc",
    bulletKeys: ["schulungen.c2_b1", "schulungen.c2_b2", "schulungen.c2_b3", "schulungen.c2_b4", "schulungen.c_hygiene"],
    forKey: "schulungen.c2_for", ctaKey: "schulungen.c2_cta",
  },
];

const INCLUDED_COUNT = 5;

const COURSE_DATES = ["2026-11-15", "2026-11-22", "2026-11-29", "2026-12-06", "2026-12-13", "2026-12-20", "2026-12-27"];
const LOCALES = { de: "de-DE", en: "en-GB", ru: "ru-RU" };
const COURSE_PRICES = { manikure: { full: 899, deposit: 269.7 }, pedikure: { full: 1199, deposit: 359.7 } };

export default function Schulungen() {
  const { t, lang } = useLang();
  const fmtDate = (iso) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(LOCALES[lang] || "de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", instagram: "" });
  const [course, setCourse] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [bookedDates, setBookedDates] = useState([]);
  const [booking, setBooking] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);

  const loadAvailability = () =>
    axios.get(`${API}/academy/availability`).then((r) => setBookedDates(r.data.booked || [])).catch(() => {});

  useEffect(() => {
    loadAvailability();
  }, []);

  const openBooking = (courseId) =>
    setBooking({ course: courseId, date: "", payment_option: "", first_name: "", last_name: "", phone: "", email: "", street: "", zip: "", city: "", instagram: "", agreed: false });

  const updBooking = (k) => (e) =>
    setBooking((b) => ({ ...b, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const fmtPrice = (n) => n.toLocaleString(LOCALES[lang] || "de-DE", { style: "currency", currency: "EUR" });
  const fmtDateShort = (iso) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(LOCALES[lang] || "de-DE", { weekday: "short", day: "numeric", month: "short" });

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!booking.date) return toast.error(t("academy.err_date"));
    if (!booking.payment_option) return toast.error(t("academy.err_payment"));
    const first = booking.first_name.trim();
    const last = booking.last_name.trim();
    const phone = booking.phone.trim();
    const email = booking.email.trim();
    const instagram = booking.instagram.trim();
    if (first.length < 2 || last.length < 2) return toast.error(t("schulungen.err_name"));
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7 || digits.length > 15) return toast.error(t("schulungen.err_phone"));
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(email)) return toast.error(t("schulungen.err_email"));
    if (!booking.street.trim() || !booking.zip.trim() || !booking.city.trim()) return toast.error(t("academy.err_address"));
    if (instagram && !/^@?[A-Za-z0-9._]{2,30}$/.test(instagram)) return toast.error(t("buchung.err_instagram_invalid"));
    if (!booking.agreed) return toast.error(t("buchung.err_agb"));
    setBookingLoading(true);
    try {
      const r = await axios.post(`${API}/academy/checkout`, {
        course: booking.course,
        date: booking.date,
        payment_option: booking.payment_option,
        first_name: first,
        last_name: last,
        email,
        phone,
        street: booking.street.trim(),
        zip: booking.zip.trim(),
        city: booking.city.trim(),
        instagram,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error(t("academy.err_taken"));
        loadAvailability();
      } else {
        toast.error(t("schulungen.err_generic"));
      }
      setBookingLoading(false);
    }
  };

  const scrollTo = (id, courseId) => {
    if (courseId) setCourse(courseId);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const first = form.first_name.trim();
    const last = form.last_name.trim();
    const phone = form.phone.trim();
    const email = form.email.trim();
    const instagram = form.instagram.trim();
    if (!course) return toast.error(t("schulungen.err_course"));
    if (first.length < 2 || last.length < 2) return toast.error(t("schulungen.err_name"));
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7 || digits.length > 15) return toast.error(t("schulungen.err_phone"));
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(email)) return toast.error(t("schulungen.err_email"));
    if (instagram && !/^@?[A-Za-z0-9._]{2,30}$/.test(instagram)) return toast.error(t("buchung.err_instagram_invalid"));

    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        first_name: first,
        last_name: last,
        email,
        phone,
        service: course === "manikure" ? "Schulung: Maniküre" : "Schulung: Pediküre",
        notes: instagram ? `Instagram: ${instagram.startsWith("@") ? instagram : "@" + instagram}` : "",
      });
      setJoined(true);
      toast.success(t("schulungen.success_toast"));
      setForm({ first_name: "", last_name: "", phone: "", email: "", instagram: "" });
      setCourse("");
    } catch {
      toast.error(t("schulungen.err_generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="schulungen-page" className="bg-background">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden pt-24 pb-20">
        <img src={IMAGES.schulungen} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/85" />
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 md:px-10 text-primary-foreground">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-primary-foreground/70">{t("schulungen.eyebrow")}</span>
          </Reveal>
          <h1 className="font-serif font-light tracking-tighter leading-[0.98] text-5xl md:text-7xl lg:text-8xl mt-6">
            <MaskReveal lines={[t("schulungen.hero_title_a"), t("schulungen.hero_title_b"), t("schulungen.hero_title_c")]} delay={0.15} />
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-8 font-serif italic text-xl md:text-2xl text-primary-foreground/90 max-w-xl">
              {t("schulungen.hero_q")}
            </p>
            <p className="mt-5 max-w-xl text-primary-foreground/80 leading-relaxed">
              {t("schulungen.hero_body")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                data-testid="academy-cta-courses"
                onClick={() => scrollTo("kurse")}
                className="group inline-flex items-center gap-3 bg-primary-foreground text-primary px-7 py-4"
              >
                <span className="text-sm tracking-wide">{t("schulungen.cta_courses")}</span>
                <ArrowRight strokeWidth={1.5} size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                data-testid="academy-cta-apply"
                onClick={() => scrollTo("anfrage")}
                className="border border-primary-foreground/40 px-7 py-4 text-sm tracking-wide transition-colors hover:border-primary-foreground"
              >
                {t("schulungen.cta_apply")}
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* KURSE */}
      <section id="kurse" data-testid="academy-courses" className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32 scroll-mt-20">
        <Reveal>
          <div className="flex items-baseline gap-4 mb-3">
            <span className="text-xs uppercase tracking-[0.25em] text-primary">{t("schulungen.courses_eyebrow")}</span>
            <span className="flex-1 border-b border-primary/30" />
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-light mb-3">{t("schulungen.courses_title")}</h2>
          <p className="text-sm text-muted-foreground mb-10 max-w-2xl">{t("schulungen.courses_note")}</p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-5">
          {COURSES.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.08}>
              <article data-testid={`course-${c.id}`} className="h-full flex flex-col p-8 md:p-10 border border-border bg-background">
                <div className="flex items-start justify-between gap-6 mb-2">
                  <div>
                    <h3 className="font-serif text-3xl md:text-4xl font-light leading-tight">{t(c.nameKey)}</h3>
                    <p className="italic text-primary/80 text-sm mt-2">{t(c.tagKey)}</p>
                  </div>
                  <span data-testid={`course-price-${c.id}`} className="font-serif text-3xl text-primary whitespace-nowrap">{t(c.priceKey)}</span>
                </div>
                <p className="text-sm text-foreground/70 leading-relaxed mt-4">{t(c.descKey)}</p>
                <div className="mt-6 pt-5 border-t border-border">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t("schulungen.learn_label")}</span>
                  <ul className="mt-3 space-y-2">
                    {c.bulletKeys.map((bk) => (
                      <li key={bk} className="flex items-start gap-2.5 text-sm text-foreground/85 leading-relaxed">
                        <Check strokeWidth={1.5} size={15} className="mt-0.5 shrink-0 text-primary" />
                        <span>{t(bk)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="mt-5 text-xs text-foreground/50 leading-relaxed">{t(c.forKey)}</p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid={`course-book-${c.id}`}
                    onClick={() => openBooking(c.id)}
                    className="group flex-1 inline-flex items-center justify-between gap-3 px-5 py-3 text-sm tracking-wide bg-primary text-primary-foreground transition-opacity hover:opacity-90"
                  >
                    <span>{t("schulungen.book_now")}</span>
                    <ArrowRight strokeWidth={1.5} size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                  <button
                    data-testid={`course-apply-${c.id}`}
                    onClick={() => scrollTo("anfrage", c.id)}
                    className="group flex-1 inline-flex items-center justify-between gap-3 px-5 py-3 text-sm tracking-wide border border-foreground/30 transition-colors hover:border-foreground"
                  >
                    <span>{t(c.ctaKey)}</span>
                    <ArrowRight strokeWidth={1.5} size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TERMINE */}
      <section data-testid="academy-dates" className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24 md:pb-32">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <Reveal className="md:col-span-5">
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("schulungen.dates_eyebrow")}</span>
            <h2 className="font-serif font-light text-4xl md:text-5xl tracking-tight mt-4">{t("schulungen.dates_title")}</h2>
            <p className="text-sm text-foreground/60 leading-relaxed mt-6 max-w-sm">{t("schulungen.dates_note")}</p>
            <ul className="mt-8 space-y-3 text-sm text-foreground/75">
              {["schulungen.dates_start", "schulungen.dates_break", "schulungen.dates_food"].map((k) => (
                <li key={k} className="flex items-center gap-3">
                  <Check strokeWidth={1.5} size={15} className="shrink-0 text-primary" />
                  <span>{t(k)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 border border-border bg-muted/40 p-5">
              <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t("schulungen.dates_bring_label")}</span>
              <p className="text-sm text-foreground/75 leading-relaxed mt-2">{t("schulungen.dates_bring")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7">
            <ul className="border-t border-border" data-testid="academy-dates-list">
              {COURSE_DATES.map((d) => (
                <li key={d} data-testid={`academy-date-${d}`} className="flex items-baseline justify-between gap-4 border-b border-border py-4">
                  <span className={`font-serif text-xl md:text-2xl font-light ${bookedDates.includes(d) ? "line-through text-muted-foreground/60" : ""}`}>{fmtDate(d)}</span>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {bookedDates.includes(d) ? t("academy.date_taken") : t("schulungen.dates_start")}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => openBooking("manikure")}
                data-testid="dates-book-manikure"
                className="group flex-1 inline-flex items-center justify-between gap-3 px-5 py-4 text-sm tracking-wide bg-primary text-primary-foreground transition-opacity hover:opacity-90"
              >
                <span>{t("schulungen.book_c1")}</span>
                <ArrowRight strokeWidth={1.5} size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => openBooking("pedikure")}
                data-testid="dates-book-pedikure"
                className="group flex-1 inline-flex items-center justify-between gap-3 px-5 py-4 text-sm tracking-wide bg-primary text-primary-foreground transition-opacity hover:opacity-90"
              >
                <span>{t("schulungen.book_c2")}</span>
                <ArrowRight strokeWidth={1.5} size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
            <button
              data-testid="dates-enquire"
              onClick={() => scrollTo("anfrage")}
              className="group mt-3 inline-flex items-center gap-2 text-sm text-primary"
            >
              <span>{t("schulungen.dates_enquire")}</span>
              <ArrowRight strokeWidth={1.5} size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* BEI JEDER SCHULUNG INKLUSIVE */}
      <section data-testid="academy-included" className="bg-muted">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("schulungen.incl_eyebrow")}</span>
            <h2 className="font-serif font-light text-4xl md:text-5xl tracking-tight mt-4">{t("schulungen.incl_title")}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 mt-14">
            {Array.from({ length: INCLUDED_COUNT }, (_, i) => i + 1).map((n, i) => (
              <Reveal key={n} delay={i * 0.06}>
                <div data-testid={`academy-included-${n}`} className="border-t border-border pt-5">
                  <span className="text-xs tracking-[0.2em] text-primary">{String(n).padStart(2, "0")}</span>
                  <h3 className="font-serif text-xl md:text-2xl font-light mt-3">{t(`schulungen.incl${n}_title`)}</h3>
                  <p className="text-sm text-foreground/65 leading-relaxed mt-2">{t(`schulungen.incl${n}_body`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHIE */}
      <section data-testid="academy-philosophy" className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <div className="max-w-3xl">
          <Reveal>
            <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight leading-[1.05]">{t("schulungen.philo_title")}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base md:text-lg text-foreground/75 leading-relaxed">{t("schulungen.philo_p1")}</p>
            <p className="mt-5 text-base md:text-lg text-foreground/75 leading-relaxed">{t("schulungen.philo_p2")}</p>
            <p className="mt-5 text-base md:text-lg text-foreground/75 leading-relaxed">{t("schulungen.philo_p3")}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-14 pt-8 border-t border-border">
              <span className="text-xs uppercase tracking-[0.3em] text-primary">Maschas Atelier Academy</span>
              <p className="font-serif italic text-xl md:text-2xl text-foreground/80 mt-4">{t("schulungen.closing")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ANFRAGE */}
      <section id="anfrage" data-testid="academy-inquiry" className="relative overflow-hidden scroll-mt-20">
        <img src={IMAGES.schulungenCard} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/90" />
        <div className="relative z-10 mx-auto max-w-[700px] px-5 md:px-10 py-24 md:py-28 text-primary-foreground">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-primary-foreground/70">{t("schulungen.form_eyebrow")}</span>
            <h2 className="font-serif font-light text-4xl md:text-5xl tracking-tight mt-4">{t("schulungen.form_title")}</h2>
            <p className="mt-5 text-primary-foreground/80 leading-relaxed max-w-lg">{t("schulungen.form_body")}</p>
          </Reveal>
          <Reveal delay={0.1}>
            {joined ? (
              <p data-testid="schulungen-joined" className="mt-10 font-serif text-2xl">{t("schulungen.joined")}</p>
            ) : (
              <form onSubmit={submit} className="mt-10 flex flex-col gap-4 w-full">
                <span className="text-xs uppercase tracking-[0.25em] text-primary-foreground/60">
                  {t("schulungen.course_label")}
                </span>
                <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label={t("schulungen.course_label")}>
                  {[
                    { id: "manikure", label: t("schulungen.course_manikure") },
                    { id: "pedikure", label: t("schulungen.course_pedikure") },
                  ].map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`schulungen-course-${c.id}`}
                      onClick={() => setCourse(c.id)}
                      aria-pressed={course === c.id}
                      className={`px-4 py-4 text-sm tracking-wide border transition-colors ${
                        course === c.id
                          ? "bg-primary-foreground text-primary border-primary-foreground"
                          : "bg-transparent text-primary-foreground/80 border-primary-foreground/30 hover:border-primary-foreground/60"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input data-testid="waitlist-first" placeholder={t("book.wl_first")} value={form.first_name} onChange={update("first_name")}
                    className="h-14 rounded-none bg-primary-foreground/10 border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/50" />
                  <Input data-testid="waitlist-last" placeholder={t("book.wl_last")} value={form.last_name} onChange={update("last_name")}
                    className="h-14 rounded-none bg-primary-foreground/10 border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/50" />
                </div>
                <Input data-testid="waitlist-phone" type="tel" placeholder={t("book.wl_phone")} value={form.phone} onChange={update("phone")}
                  className="h-14 rounded-none bg-primary-foreground/10 border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/50" />
                <Input data-testid="waitlist-email" type="email" placeholder={t("book.wl_email")} value={form.email} onChange={update("email")}
                  className="h-14 rounded-none bg-primary-foreground/10 border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/50" />
                <Input data-testid="waitlist-instagram" placeholder={t("buchung.instagram")} value={form.instagram} onChange={update("instagram")}
                  className="h-14 rounded-none bg-primary-foreground/10 border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/50" />
                <button
                  type="submit"
                  data-testid="waitlist-submit"
                  disabled={loading}
                  className="group flex items-center justify-center gap-3 bg-primary-foreground text-primary px-7 py-4 whitespace-nowrap disabled:opacity-60"
                >
                  <span className="text-sm tracking-wide">{loading ? "…" : t("schulungen.notify")}</span>
                  <ArrowRight strokeWidth={1.5} size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
      {/* BUCHUNGS-DIALOG */}
      <Dialog open={!!booking} onOpenChange={(open) => { if (!open) setBooking(null); }}>
        <DialogContent data-testid="booking-dialog" data-lenis-prevent className="dialog-scroll rounded-none max-w-lg">
          {booking && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif font-light text-3xl tracking-tight">{t("academy.dialog_title")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submitBooking} className="flex flex-col gap-5 mt-2">
                <div className="grid grid-cols-2 gap-2">
                  {COURSES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`book-course-${c.id}`}
                      onClick={() => setBooking((b) => ({ ...b, course: c.id }))}
                      aria-pressed={booking.course === c.id}
                      className={`px-4 py-3 text-sm tracking-wide border transition-colors ${
                        booking.course === c.id
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-foreground/80 hover:border-foreground/50"
                      }`}
                    >
                      {t(c.nameKey)}
                    </button>
                  ))}
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t("academy.choose_date")}</span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                    {COURSE_DATES.map((d) => {
                      const taken = bookedDates.includes(d);
                      return (
                        <button
                          key={d}
                          type="button"
                          disabled={taken}
                          data-testid={`book-date-${d}`}
                          onClick={() => setBooking((b) => ({ ...b, date: d }))}
                          className={`px-3 py-2.5 text-sm border transition-colors ${
                            taken
                              ? "border-border text-muted-foreground/50 line-through cursor-not-allowed"
                              : booking.date === d
                                ? "bg-primary text-primary-foreground border-primary"
                                : "border-border text-foreground/80 hover:border-foreground/50"
                          }`}
                        >
                          {fmtDateShort(d)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t("academy.payment_label")}</span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    {["full", "deposit"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        data-testid={`book-pay-${opt}`}
                        onClick={() => setBooking((b) => ({ ...b, payment_option: opt }))}
                        aria-pressed={booking.payment_option === opt}
                        className={`px-4 py-3 text-left border transition-colors ${
                          booking.payment_option === opt
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border hover:border-foreground/50"
                        }`}
                      >
                        <span className="block text-sm font-medium">{t(`academy.pay_${opt}`)}</span>
                        <span className="block font-serif text-xl mt-1">{fmtPrice(COURSE_PRICES[booking.course][opt])}</span>
                        <span className={`block text-xs mt-1 ${booking.payment_option === opt ? "text-primary-foreground/75" : "text-muted-foreground"}`}>
                          {t(`academy.pay_${opt}_note`)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input data-testid="book-first" placeholder={t("book.wl_first")} value={booking.first_name} onChange={updBooking("first_name")} className="h-12 rounded-none" />
                  <Input data-testid="book-last" placeholder={t("book.wl_last")} value={booking.last_name} onChange={updBooking("last_name")} className="h-12 rounded-none" />
                </div>
                <Input data-testid="book-phone" type="tel" placeholder={t("book.wl_phone")} value={booking.phone} onChange={updBooking("phone")} className="h-12 rounded-none" />
                <Input data-testid="book-email" type="email" placeholder={t("book.wl_email")} value={booking.email} onChange={updBooking("email")} className="h-12 rounded-none" />
                <Input data-testid="book-street" placeholder={t("academy.street")} value={booking.street} onChange={updBooking("street")} className="h-12 rounded-none" />
                <div className="grid grid-cols-[1fr_2fr] gap-3">
                  <Input data-testid="book-zip" inputMode="numeric" placeholder={t("academy.zip")} value={booking.zip} onChange={updBooking("zip")} className="h-12 rounded-none" />
                  <Input data-testid="book-city" placeholder={t("academy.city")} value={booking.city} onChange={updBooking("city")} className="h-12 rounded-none" />
                </div>
                <Input data-testid="book-instagram" placeholder={t("buchung.instagram")} value={booking.instagram} onChange={updBooking("instagram")} className="h-12 rounded-none" />
                <label className="flex items-start gap-3 text-xs text-muted-foreground leading-relaxed cursor-pointer">
                  <input type="checkbox" data-testid="book-agb" checked={booking.agreed} onChange={updBooking("agreed")} className="mt-0.5 accent-primary" />
                  <span>
                    {t("buchung.agb_prefix")}{" "}
                    <a href="/agb" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                      {t("academy.agb_link")}
                    </a>{" "}
                    {t("buchung.agb_suffix")}
                  </span>
                </label>
                <button
                  type="submit"
                  data-testid="book-submit"
                  disabled={bookingLoading}
                  className="group flex items-center justify-center gap-3 bg-primary text-primary-foreground px-7 py-4 transition-opacity hover:opacity-90 disabled:opacity-60"
                >
                  <span className="text-sm tracking-wide">{bookingLoading ? "…" : t("academy.to_checkout")}</span>
                  <ArrowRight strokeWidth={1.5} size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <p className="text-[11px] text-muted-foreground text-center -mt-2">{t("academy.secure_note")}</p>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
